import React, { useState } from "react";
import { Phone } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import ReuseableDrawer from "../../../../components/resuable-drawer";
import { LiveCallRow } from "../types";

const SENTIMENT_STYLES: Record<string, string> = {
  Positive: "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  Neutral: "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300",
  Negative: "bg-rose-50 dark:bg-rose-500/15 text-rose-600 dark:text-rose-400",
};

// "View Full Transcript" — a resizable/fullscreen-toggleable right-side
// drawer, same ReuseableDrawer used for the campaign Report (see
// pages/campaign/index.tsx), rather than navigating away from the page.
export const TranscriptDrawer: React.FC<{
  call: LiveCallRow | null;
  onClose: () => void;
}> = ({ call, onClose }) => {
  const [tab, setTab] = useState<"transcript" | "insights">("transcript");

  return (
    <ReuseableDrawer isOpen={!!call} onClose={onClose} title={call ? `Call with ${call.from}` : "Call Transcript"}>
      {call && (
        <div className="flex flex-col h-full">
          <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800">
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center shrink-0">
                {call.channel === "whatsapp" ? <FaWhatsapp className="text-green-500" size={16} /> : <Phone className="text-indigo-500" size={16} />}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{call.from}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">To {call.to}</div>
              </div>
              <div className="ml-auto text-xs text-gray-500 dark:text-gray-400 text-right">
                <div>Handled by <span className="font-medium text-gray-700 dark:text-gray-300">{call.agentName}</span></div>
                <div>Duration: {call.duration}</div>
              </div>
            </div>
          </div>

          <div className="flex border-b border-gray-100 dark:border-gray-800 px-4 sm:px-6 shrink-0">
            {(["transcript", "insights"] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`px-3 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab === t
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                }`}
              >
                {t === "transcript" ? "Transcript" : "Call Insights"}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {tab === "transcript" ? (
              call.transcript && call.transcript.length > 0 ? (
                <div className="space-y-4">
                  {call.transcript.map((m, i) => (
                    <div key={i} className={`flex flex-col ${m.speaker === "agent" ? "items-start" : "items-end"}`}>
                      <div
                        className={`max-w-[85%] sm:max-w-[75%] rounded-xl px-3.5 py-2.5 text-sm ${
                          m.speaker === "agent"
                            ? "bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                            : "bg-indigo-600 text-white"
                        }`}
                      >
                        {m.text}
                      </div>
                      <span className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1 px-1">
                        {m.name} · {m.time}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">No transcript is available for this call.</p>
              )
            ) : (
              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Detected Intent</h4>
                  <p className="text-sm text-gray-800 dark:text-gray-200">{call.intent || "N/A"}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Sentiment</h4>
                  {call.sentiment ? (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${SENTIMENT_STYLES[call.sentiment]}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-current" /> {call.sentiment}
                    </span>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">N/A</p>
                  )}
                </div>
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5">Call Summary</h4>
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{call.summary || "No summary available."}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </ReuseableDrawer>
  );
};

export default TranscriptDrawer;
